import { Either, Schema } from "effect";
import { CurriculumLanguage } from "./curriculum-tags.ts";
import {
  activePersonas,
  PERSONA_IMAGE_SLOTS,
  PERSONAS_FILE,
  PersonaId,
  type PersonaRole,
  type PersonasFile,
  parsePersonaImagePath,
  parsePersonasFile,
  renderPersonasFile,
  SCHEMA_PERSONA_IDS,
} from "./personas.ts";

// ADR-0111 — "is this language pack complete enough to generate against, and if not, what exactly
// is missing?"
//
// One pure function, here in `core`, because the same answer is needed in three places that must
// never disagree: the upload form (before anything is written), the "Language taught" select in the
// New Curriculum modal (an L2 whose pack is incomplete must not be offered), and the server's own
// refusal to accept an upload. A second copy of these rules in the web app would drift the moment
// the pipeline's input check changes.
//
// The rules are derived from what actually fails a run, NOT from the prose in
// `L2__TEMPLATE__/README.md`:
//
//   * BLOCKER = `l2_unit_plan/scripts/run_pipeline.py::check_inputs` calls `sys.exit` on it. That is
//     the per-file existence list (`PERSONAS`, `REFERENCE_DIALOGUES`, `LANGUAGE_PROFILE` from the L2
//     pack; `L1_PROFILE`, `TUTORS` from the L1 pack) plus the registry, which both runtimes hard-exit
//     without. A blocker means the launch dies before a cluster does any work.
//   * WARNING = the run starts and degrades, usually silently. The sharpest of these is
//     `language_rules.py`: with `function_words.yml` and friends absent it falls back to SPANISH
//     defaults rather than failing, so an Italian pack missing them produces plausible-looking
//     Spanish-shaped output. Exactly the class of failure a warning has to be loud about.
//   * INFO = something this validator cannot decide and a human should know about, rather than
//     silence that reads as approval.
//
// ADR-0118 moves the persona registry into the pack (`personas.yml`), so the cast gate is no longer
// deferred to launch: every `## <Name>` in `personas.md` / `tutors.md` is resolved against the
// pack's own registry here, and an unknown name is a blocker at upload rather than a launch failure.
//
// Deliberately NOT a blocker: the vocab `.zip` and the grammar ledgers. The zip is binary and is not
// even a valid `LanguagePackPath`, and the ledgers are pipeline OUTPUT (step 0 generates them into
// `l2_unit_plan/generated/`), so requiring either at upload time would make every honest pack invalid.

export const PackFindingSeverity = Schema.Literal("blocker", "warning", "info");
export type PackFindingSeverity = typeof PackFindingSeverity.Type;

export const PackFinding = Schema.Struct({
  severity: PackFindingSeverity,
  // The pack-relative path the finding is about (`L2_FRA/personas.md`), or the registry, or absent
  // when the finding is about the upload as a whole.
  path: Schema.optional(Schema.String),
  // What is wrong, stated as a fact.
  message: Schema.String,
  // What to do about it. Every finding carries one — a report that says only "personas.md is
  // missing" makes the reader go and find the template themselves, which is the work this is
  // supposed to remove.
  fix: Schema.String,
});
export type PackFinding = typeof PackFinding.Type;

/** One file of an upload: its pack-relative path and its text. */
export const PackFile = Schema.Struct({
  path: Schema.String,
  content: Schema.String,
});
export type PackFile = typeof PackFile.Type;

export const PackReport = Schema.Struct({
  l2: Schema.String,
  l1: Schema.optional(Schema.String),
  findings: Schema.Array(PackFinding),
  // `true` when nothing is a blocker — the pack may be written, and a run may be launched against
  // it. Warnings never gate: the standing rule from the generation working session is that only
  // checks whose failure produces an unusable result block, everything else informs.
  complete: Schema.Boolean,
});
export type PackReport = typeof PackReport.Type;

const hasBlocker = (findings: readonly PackFinding[]): boolean =>
  findings.some((finding) => finding.severity === "blocker");

// `<!-- pronoun-noise: i, you, he, … -->` in the L1 profile. Absent, the match-exercise parser in
// `convert_unit_plan_md_to_json.py` silently falls back to the ENGLISH pronoun set — so this matters
// for any L1 that is not English, and is harmless for one that is. It is reported at one severity
// regardless, because this validator does not know which L1 is which.
const PRONOUN_NOISE_MARKER = /<!--\s*pronoun-noise:/;

// `dialog_polish.py` parses `personas.md` by H2, and excludes exactly these two headings from the
// persona set BY NAME. Rename either and its content is parsed as a persona named "Learner
// positioning" — which then fails the cast gate at launch with a confusing message.
const REQUIRED_PERSONA_SECTIONS = ["## Learner positioning", "## Cast distribution rules"] as const;

// By prefix: the committed packs title the second one `## Cast distribution rules (for the planner)`,
// and treating that as a persona would report it as a cast member missing every field.
const isReservedSection = (heading: string): boolean =>
  REQUIRED_PERSONA_SECTIONS.some((section) => heading.startsWith(section));

// The six bold fields the template requires of every persona block. Missing ones do not fail a
// launch, they flatten the generated dialogue — the pack is structurally fine and the output is
// generic, which is the failure mode hardest to spot after the fact.
const PERSONA_FIELDS = [
  "**Age / origin:**",
  "**Role:**",
  "**Register with learner:**",
  "**Voice / quirks:**",
  "**Regional / dialect features:**",
  "**Default scene contexts",
] as const;

/** The token every unfilled slot carries, so `checkLanguagePack` can find the ones left behind. */
export const FILL_MARKER = "<<FILL:";

// The marker `validate_tutors_md` keys on when it collects tutor names.
const TUTOR_VOICE_MARKER = "**Voice / tone:**";

const h2Headings = (content: string): readonly string[] =>
  content
    .split("\n")
    .filter((line) => line.startsWith("## "))
    .map((line) => line.trim());

/** The `## <Name>` blocks that are cast entries — everything but the two reserved section titles. */
const castNames = (content: string): readonly string[] =>
  h2Headings(content)
    .filter((heading) => !isReservedSection(heading))
    .map((heading) => heading.slice(3).trim());

/** Split a file on its `## ` headings, so a per-persona check sees only that persona's block. */
const blocksOf = (content: string): readonly { readonly name: string; readonly body: string }[] => {
  const blocks: { name: string; body: string }[] = [];
  for (const line of content.split("\n")) {
    if (line.startsWith("## ")) {
      blocks.push({ name: line.slice(3).trim(), body: "" });
      continue;
    }
    const current = blocks.at(-1);
    if (current !== undefined) current.body += `${line}\n`;
  }
  return blocks;
};

const registryHasCode = (registry: string, code: string): boolean =>
  registry.split("\n").some((line) => line.trim().startsWith(`${code}:`));

// The five per-language rule files the Python runtime reads (`language_packs/__init__.py`). The
// TypeScript runtime parses the same lists out of HTML markers inside `language_profile.md` instead
// (`language-packs.ts`), so a pack can satisfy one runtime and quietly mis-serve the other — which is
// why the template writes both.
//
// Four are flat YAML lists (`- entry`). `decisive_orthography.yml` is NOT: it is one quoted string
// used verbatim as a regex character class, and `language_rules.py` runs `re.escape` over it — a
// list there crashes the run rather than falling back.
const LANGUAGE_RULE_LISTS = [
  "function_words.yml",
  "country_names.yml",
  "speaker_prelude_verbs.yml",
  "gender_pair_exclude.yml",
] as const;
const DECISIVE_ORTHOGRAPHY = "decisive_orthography.yml";
const LANGUAGE_RULE_FILES = [...LANGUAGE_RULE_LISTS, DECISIVE_ORTHOGRAPHY] as const;

// Each rule file's `<!-- key: … -->` twin in `language_profile.md`, the form the TypeScript runtime
// reads (`language-packs.ts`), plus the two markers BOTH runtimes read only from the profile.
const PROFILE_MARKERS = [
  "essential-flagship-verbs",
  "scene-demonym-stopwords",
  "l2-function-words",
  "country-names",
  "speaker-prelude-verbs",
  "gender-pair-exclude",
  "decisive-orthography",
] as const;

// `@${VOCAB_INVENTORY}` is inlined unconditionally by the step-1 prompts, so a pack with no vocab
// reference fails at render time with `FileNotFoundError` — after the cluster has started, which is
// why it is a warning here and not a launch blocker. The deployed tree may still carry a `.yml.zip`
// twin (binary, never stored in studio), which `run_pipeline.py` prefers when present.
const VOCAB_INVENTORY = "vocab/full_vocab_inventory.reference.yml";

/** The YAML lines that carry content — not blank, not a `#` comment. */
const yamlContentLines = (content: string): readonly string[] =>
  content
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0 && !line.startsWith("#"));

export interface PackCompletenessInput {
  /** The L2 code WITHOUT its `L2_` prefix, e.g. `FRA`. */
  readonly l2: string;
  /** The L1 code without its `L1_` prefix. Omitted when only the L2 side is being judged. */
  readonly l1?: string | undefined;
  /**
   * Every pack file that WILL exist once this upload lands: the uploaded files merged over whatever
   * the store already holds. The caller does the merging, so this function answers the same question
   * for an upload preview and for a pack already in the store without two code paths.
   */
  readonly files: readonly PackFile[];
  /**
   * Every image path (`L2_FRA/personas/<id>/<slot>.webp`) that WILL exist once this upload lands.
   * Omitted when the caller does not know (the readiness list, which only needs blockers): image
   * presence is then not judged at all rather than reported missing. Images only ever warn.
   */
  readonly images?: readonly string[] | undefined;
}

/**
 * Judge one (L2, L1?) pair's pack against what the pipeline requires of it.
 *
 * Pure and total: it never reads the store, the filesystem or the network, so the upload form can
 * run it on every change and the server can run the identical rules before writing.
 */
export const checkLanguagePack = (input: PackCompletenessInput): PackReport => {
  const findings: PackFinding[] = [];
  const byPath = new Map(input.files.map((file) => [file.path, file.content]));
  const l2Dir = `L2_${input.l2}`;
  const l1Dir = input.l1 === undefined ? undefined : `L1_${input.l1}`;

  const requireFile = (path: string, what: string, fix: string): string | undefined => {
    const content = byPath.get(path);
    if (content === undefined) {
      findings.push({ severity: "blocker", path, message: `${what} is missing.`, fix });
      return undefined;
    }
    if (content.trim().length === 0) {
      findings.push({ severity: "blocker", path, message: `${what} is empty.`, fix });
      return undefined;
    }
    return content;
  };

  const imagesByPersona = new Map<string, Set<string>>();
  for (const image of input.images ?? []) {
    const parts = parsePersonaImagePath(image);
    if (parts === undefined) continue;
    const key = `${parts.packDir}/${parts.personaId}`;
    imagesByPersona.set(key, (imagesByPersona.get(key) ?? new Set()).add(parts.slot));
  }

  // A pack's `personas.yml`, parsed. Missing, empty or unparsable is a blocker: every runtime that
  // voices or draws a persona reads it, and none of them falls back (ADR-0118 Decision 7).
  const readRegistry = (path: string, what: string): PersonasFile | undefined => {
    const content = requireFile(
      path,
      `The ${what} registry \`${PERSONAS_FILE}\``,
      "Download the template for this language: it carries the file with the canonical cast. Or add the personas in the Personas view.",
    );
    if (content === undefined) return undefined;
    const parsed = parsePersonasFile(content);
    if (Either.isLeft(parsed)) {
      for (const issue of parsed.left.slice(0, 10)) {
        findings.push({
          severity: "blocker",
          path,
          message: `\`${PERSONAS_FILE}\` does not parse: ${issue}.`,
          fix: "Fix the entry so it matches the shape described in the file's header comment.",
        });
      }
      return undefined;
    }
    return parsed.right;
  };

  const judgeRegistry = (packDir: string, file: PersonasFile, roles: readonly PersonaRole[]) => {
    const path = `${packDir}/${PERSONAS_FILE}`;
    const ids = new Set<string>();
    for (const persona of file.personas) {
      if (ids.has(persona.id)) {
        findings.push({
          severity: "blocker",
          path,
          message: `Persona id \`${persona.id}\` is defined twice.`,
          fix: "Keep one entry per id — the runtimes look a persona up by id, so a duplicate makes the choice arbitrary.",
        });
      }
      ids.add(persona.id);
    }
    for (const persona of file.personas) {
      if (persona.alias_of !== undefined && !ids.has(persona.alias_of)) {
        findings.push({
          severity: "blocker",
          path,
          message: `\`${persona.id}\` is an alias of \`${persona.alias_of}\`, which this file does not define.`,
          fix: "Point `alias_of` at a persona defined in the same file, or remove it.",
        });
      }
      if (persona.deprecated) continue;
      if (!roles.includes(persona.role)) {
        findings.push({
          severity: "warning",
          path,
          message: `\`${persona.id}\` has role \`${persona.role}\`, which this pack does not hold.`,
          fix: packDir.startsWith("L1_")
            ? "An L1 pack holds the tutors, who speak the L1. Move scene characters to the L2 pack's registry."
            : "Tutors speak the learner's L1, so they belong in the L1 pack's registry, not the L2's.",
        });
      }
      if (persona.voice === undefined) {
        findings.push({
          severity: "warning",
          path,
          message: `\`${persona.id}\` has no voice.`,
          fix: "Add `voice: { provider: elevenlabs, voice_id: <id>, speed: 1 }`. Without one, TTS speaks this persona in the default voice.",
        });
      }
      if (
        input.images !== undefined &&
        !imagesByPersona.get(`${packDir}/${persona.id}`)?.has("origin")
      ) {
        findings.push({
          severity: "warning",
          path: `${packDir}/personas/${persona.id}/`,
          message: `\`${persona.id}\` has no \`origin\` image.`,
          fix: "Add `personas/<id>/origin.webp` (or .png/.jpg). It is the identity reference sent to the image model; without it every picture of this persona is drawn from the description alone and will not look like the same person twice.",
        });
      }
      if (!SCHEMA_PERSONA_IDS.has(persona.id)) {
        findings.push({
          severity: "warning",
          path,
          message: `\`${persona.id}\` is a new persona id, which the exercise schema does not accept yet.`,
          fix: "Content that names this persona is refused downstream until `PersonaId` opens (ADR-0118 Decision 6). Editing an existing persona's voice, images or prose works today.",
        });
      }
    }
  };

  // Every `## <Name>` a prompt file declares must resolve to an active persona of the right role —
  // the check `validate_personas_md` / `validate_tutors_md` used to run against the Python registry
  // at launch.
  const gateNames = (
    names: readonly string[],
    path: string,
    file: PersonasFile,
    role: PersonaRole,
    registryPath: string,
  ) => {
    const known = activePersonas(file, role).map((persona) => persona.display_name);
    for (const name of names) {
      if (!known.includes(name)) {
        findings.push({
          severity: "blocker",
          path,
          message: `\`## ${name}\` is not an active \`${role}\` in \`${registryPath}\`.`,
          fix:
            known.length === 0
              ? `Add a \`${role}\` persona with display_name \`${name}\` to the registry.`
              : `Use one of ${known.map((k) => `\`${k}\``).join(", ")}, or add \`${name}\` to the registry as a \`${role}\`.`,
        });
      }
    }
  };

  // ── The registry ────────────────────────────────────────────────────────────
  // Both runtimes resolve `${L2}` / `${L1}` display names through this file and hard-exit pointing
  // at it when a code is absent. A pack whose files are all present but whose code is unregistered
  // fails at the very first step, with an error that reads as a pipeline bug rather than a missing
  // line of YAML.
  const registry = byPath.get("language_codes.yml");
  if (registry === undefined) {
    findings.push({
      severity: "blocker",
      path: "language_codes.yml",
      message: "The language-code registry is missing.",
      fix: "Include `language_codes.yml` in the upload, or upload into a studio that already holds it.",
    });
  } else {
    for (const code of [input.l2, ...(input.l1 === undefined ? [] : [input.l1])]) {
      if (!registryHasCode(registry, code)) {
        findings.push({
          severity: "blocker",
          path: "language_codes.yml",
          message: `The registry has no entry for \`${code}\`.`,
          fix: `Add a line \`${code}: { name: "<display name>" }\` — the upload form's display-name field writes it for you.`,
        });
      }
    }
  }

  // ── The L2 pack ─────────────────────────────────────────────────────────────
  requireFile(
    `${l2Dir}/language_profile.md`,
    "The pack manifest `language_profile.md`",
    "Model it on `L2_QMS/language_profile.md`: display name and code, the register system's actual forms, the A1/A2/B scene model, and a `## Grammar inventory notes` section.",
  );

  const personas = requireFile(
    `${l2Dir}/personas.md`,
    "`personas.md`",
    "Model it on `L2_QMS/personas.md`: `# Recurring user personas`, one `## <FirstName>` block per persona, then `## Learner positioning` and `## Cast distribution rules`.",
  );
  if (personas !== undefined) {
    for (const section of REQUIRED_PERSONA_SECTIONS) {
      if (!personas.includes(section)) {
        findings.push({
          severity: "blocker",
          path: `${l2Dir}/personas.md`,
          message: `The \`${section}\` section is missing.`,
          fix: "Add it with that heading text exactly — `dialog_polish` excludes these two headings from persona parsing BY NAME, so a renamed one is parsed as a persona and fails the cast gate.",
        });
      }
    }
    const cast = castNames(personas);
    if (cast.length === 0) {
      findings.push({
        severity: "blocker",
        path: `${l2Dir}/personas.md`,
        message: "No persona sections found.",
        fix: "Give each persona its own `## <FirstName>` heading — the launch-time cast gate fails an empty cast file outright.",
      });
    } else {
      if (cast.length < 4) {
        findings.push({
          severity: "warning",
          path: `${l2Dir}/personas.md`,
          message: `Only ${cast.length} persona${cast.length === 1 ? "" : "s"} (${cast.join(", ")}); the convention is 4 main characters.`,
          fix: "Add personas until the cast covers mentor, peer-friend, community connector and teacher — a thin cast makes every unit sound like the same speaker.",
        });
      }
      for (const block of blocksOf(personas)) {
        if (isReservedSection(`## ${block.name}`)) continue;
        const absent = PERSONA_FIELDS.filter((field) => !block.body.includes(field));
        if (absent.length > 0) {
          findings.push({
            severity: "warning",
            path: `${l2Dir}/personas.md`,
            message: `Persona \`${block.name}\` is missing ${absent.join(", ")}.`,
            fix: "Fill every field to the depth of the `L2_QMS` cast — named neighbourhoods, concrete dialect features, real lexical items. A structurally valid but generically written persona produces flat dialogue.",
          });
        }
      }
    }
  }

  // ── The persona registry (ADR-0118) ─────────────────────────────────────────
  const l2Registry = readRegistry(`${l2Dir}/${PERSONAS_FILE}`, "scene cast");
  if (l2Registry !== undefined) {
    judgeRegistry(l2Dir, l2Registry, ["main-character", "extra"]);
    if (personas !== undefined) {
      gateNames(
        castNames(personas),
        `${l2Dir}/personas.md`,
        l2Registry,
        "main-character",
        `${l2Dir}/${PERSONAS_FILE}`,
      );
    }
  }

  requireFile(
    `${l2Dir}/reference_dialogues.md`,
    "`reference_dialogues.md`",
    "Provide the dialogue exemplars `dialog_polish` writes against; model them on `L2_QMS/reference_dialogues.md`.",
  );

  // ── The L1 pack ─────────────────────────────────────────────────────────────
  // Skipped entirely when no L1 is named: an upload that only defines an L2 is a legitimate thing to
  // do (the L1 side is shared across every L2 that audience learns, and usually exists already). The
  // New Curriculum modal asks this question again per PAIR, where the L1 is known.
  if (l1Dir !== undefined) {
    const l1Profile = requireFile(
      `${l1Dir}/profile.md`,
      "The L1 profile",
      "Model it on `L1_QAE/profile.md`: the gloss conventions for this L1, plus the machine-read pronoun-noise marker.",
    );
    if (l1Profile !== undefined && !PRONOUN_NOISE_MARKER.test(l1Profile)) {
      findings.push({
        severity: "warning",
        path: `${l1Dir}/profile.md`,
        message: "The `pronoun-noise` marker is missing.",
        fix: "Add `<!-- pronoun-noise: i, you, he, she, we, they, it -->` with THIS L1's subject pronouns, lowercase. Without it the match-exercise parser silently falls back to the English set.",
      });
    }

    const tutors = requireFile(
      `${l1Dir}/tutors.md`,
      "`tutors.md`",
      "Model it on `L1_QAE/tutors.md`: one `## <FirstName>` block per tutoring voice, each declaring `**Voice / tone:**`.",
    );
    if (tutors !== undefined) {
      const voices = blocksOf(tutors).filter((block) => block.body.includes(TUTOR_VOICE_MARKER));
      if (voices.length === 0) {
        findings.push({
          severity: "blocker",
          path: `${l1Dir}/tutors.md`,
          message: "No tutor sections found.",
          fix: "Give each tutor a `## <FirstName>` heading and a `**Voice / tone:**` line — `validate_tutors_md` keys on that marker and exits when it finds none.",
        });
      }
    }

    const l1Registry = readRegistry(`${l1Dir}/${PERSONAS_FILE}`, "tutor");
    if (l1Registry !== undefined) {
      judgeRegistry(l1Dir, l1Registry, ["tutor"]);
      if (tutors !== undefined) {
        gateNames(
          blocksOf(tutors)
            .filter((block) => block.body.includes(TUTOR_VOICE_MARKER))
            .map((block) => block.name),
          `${l1Dir}/tutors.md`,
          l1Registry,
          "tutor",
          `${l1Dir}/${PERSONAS_FILE}`,
        );
      }
      if (l2Registry !== undefined) {
        const l2Ids = new Set(l2Registry.personas.map((persona) => persona.id));
        for (const persona of l1Registry.personas.filter((p) => l2Ids.has(p.id))) {
          findings.push({
            severity: "blocker",
            path: `${l1Dir}/${PERSONAS_FILE}`,
            message: `Persona id \`${persona.id}\` is defined in both \`${l2Dir}\` and \`${l1Dir}\`.`,
            fix: "A run merges the two registries, so an id may live in only one of them: scene characters in the L2 pack, tutors in the L1 pack.",
          });
        }
      }
    }
  }

  // ── Degrade-silently warnings ───────────────────────────────────────────────
  // A file that exists but lists nothing is the same thing to the runtime as no file at all: it
  // reads an empty list and falls back to the Spanish defaults. Checking only for presence would
  // pass the skeleton this validator's own template generates, which is the one case where a
  // reassuring report is most likely to be believed.
  const orthography = byPath.get(`${l2Dir}/${DECISIVE_ORTHOGRAPHY}`);
  if (orthography !== undefined && yamlContentLines(orthography).some((l) => l.startsWith("- "))) {
    findings.push({
      severity: "blocker",
      path: `${l2Dir}/${DECISIVE_ORTHOGRAPHY}`,
      message: `\`${DECISIVE_ORTHOGRAPHY}\` is a list; it must be one quoted string.`,
      fix: 'Write the characters as a single quoted string, e.g. `"àèìòùé"`. `language_rules.py` runs `re.escape` over this value, so a YAML list crashes the run.',
    });
  }
  for (const name of LANGUAGE_RULE_FILES) {
    const rules = byPath.get(`${l2Dir}/${name}`);
    const lines = rules === undefined ? [] : yamlContentLines(rules);
    const empty =
      rules !== undefined &&
      (name === DECISIVE_ORTHOGRAPHY
        ? lines.every((line) => /^["']?["']?$/.test(line))
        : !lines.some((line) => line.startsWith("- ")));
    if (rules === undefined || empty) {
      findings.push({
        severity: "warning",
        path: `${l2Dir}/${name}`,
        message: `\`${name}\` ${rules === undefined ? "is absent" : "lists no entries"}.`,
        fix: "The Python runtime falls back to SPANISH defaults for this list rather than failing, so the run will look fine and be wrong. Copy the `L2_ITA` file and replace its entries.",
      });
    }
  }

  // The template ships every file pre-filled with `<<FILL: …>>` markers. Uploading one with markers
  // still in it is the likeliest way to get a structurally valid, semantically empty pack — every
  // heading present, nothing said — so each such file is named rather than passing quietly.
  for (const file of input.files) {
    if (file.content.includes(FILL_MARKER)) {
      const count = file.content.split(FILL_MARKER).length - 1;
      findings.push({
        severity: "warning",
        path: file.path,
        message: `Still has ${count} unfilled \`${FILL_MARKER}…>>\` placeholder${count === 1 ? "" : "s"} from the template.`,
        fix: "Replace each one with real content, to the depth of the `L2_QMS` pack — concrete dialect features, named places, real lexical items. A pack whose placeholders survive generates flat, generic material.",
      });
    }
  }

  const profile = byPath.get(`${l2Dir}/language_profile.md`);
  if (profile !== undefined && !profile.includes("## Grammar inventory notes")) {
    findings.push({
      severity: "warning",
      path: `${l2Dir}/language_profile.md`,
      message: "No `## Grammar inventory notes` section.",
      fix: "Steps 0s/0a read it (verb allowlist, deferrals, example register) when generating this L2's grammar inventory. With no upstream inventory record for this L2, the generated arm is the only route and this section is its only input.",
    });
  }
  if (profile !== undefined) {
    const absent = PROFILE_MARKERS.filter(
      (key) => !new RegExp(`<!--\\s*${key}\\s*:`, "i").test(profile),
    );
    if (absent.length > 0) {
      findings.push({
        severity: "warning",
        path: `${l2Dir}/language_profile.md`,
        message: `No \`<!-- ${absent.join(": … -->`, `<!-- ")}: … -->\` marker${absent.length === 1 ? "" : "s"}.`,
        fix: "Add them under `## Parser configuration`. The TypeScript runtime reads these lists ONLY from the profile markers (not the `.yml` files) and keeps the Spanish defaults for any it cannot find.",
      });
    }
  }

  if (!byPath.has(`${l2Dir}/${VOCAB_INVENTORY}`)) {
    findings.push({
      severity: "warning",
      path: `${l2Dir}/${VOCAB_INVENTORY}`,
      message: "No vocab reference inventory.",
      fix: "Step 1 inlines it unconditionally, so a run without it fails with `FileNotFoundError` once the cluster is up. Provide an `items:` list of rows with `item_<L2>`, `item_<L1>`, `part of speech`, `CEFR` and `gender` — the template's file shows the shape.",
    });
  }

  findings.push({
    severity: "info",
    message: "The grammar ledger is generated, not uploaded.",
    fix: `Run \`run_pipeline.py --l2 ${input.l2} --l1 ${input.l1 ?? "<L1>"} --cefr A1.1 --step 0\` once per pair × CEFR before the first full run; if \`curriculum_grammars.json\` has no record for this L2, add \`--grammar-source generated\`.`,
  });

  return { l2: input.l2, l1: input.l1, findings, complete: !hasBlocker(findings) };
};

/** Every language code present in a set of pack paths, split by side. */
export const languageCodesOfPaths = (
  paths: readonly string[],
): { readonly l1: readonly string[]; readonly l2: readonly string[] } => {
  const l1 = new Set<string>();
  const l2 = new Set<string>();
  for (const path of paths) {
    const [root] = path.split("/");
    if (root === undefined) continue;
    if (root.startsWith("L1_")) l1.add(root.slice(3));
    if (root.startsWith("L2_")) l2.add(root.slice(3));
  }
  return { l1: [...l1].sort(), l2: [...l2].sort() };
};

/**
 * Normalise a path as a browser hands it over, into a pack-relative path.
 *
 * A directory picker yields `webkitRelativePath` rooted at whatever the human selected: the `packs/`
 * directory, one `L2_FRA/` directory, or a folder with an arbitrary name. All three must land on the
 * same pack-relative path, so this drops every leading segment before the first `L1_`/`L2_` root —
 * or, for the registry, keeps only its filename. Returns `undefined` for a path with no
 * pack-relative interpretation (a stray `.DS_Store`, an image, a README).
 */
export const packRelativePath = (rawPath: string): string | undefined => {
  const segments = rawPath.split("/").filter((segment) => segment.length > 0);
  // The WHOLE segment must be a language root, the shape `LanguagePackPath` admits. A prefix match
  // would anchor on a wrapper folder such as `L2_FRA-pack/` — the name the template archive used to
  // unpack to — and every file under it would then be refused as not a pack document.
  const rootIndex = segments.findIndex((segment) => /^L[12]_[A-Za-z]+$/.test(segment));
  if (rootIndex >= 0) {
    const relative = segments.slice(rootIndex).join("/");
    return relative.endsWith(".md") || relative.endsWith(".yml") ? relative : undefined;
  }
  return segments.at(-1) === "language_codes.yml" ? "language_codes.yml" : undefined;
};

/**
 * The `CODE -> display name` pairs in `language_codes.yml`.
 *
 * Parsed with a line regex rather than a YAML library: the file's own header documents exactly one
 * admissible shape (`<CODE>: { name: "<display name>" }`), studio only ever writes that shape, and
 * pulling a YAML parser into `core` for four lines of flow mapping would be the larger liability.
 * A line that does not match is skipped, so comments and blanks cost nothing.
 */
export const parseLanguageRegistry = (
  content: string,
): readonly { readonly code: string; readonly name: string }[] => {
  const entries: { code: string; name: string }[] = [];
  for (const line of content.split("\n")) {
    const match = /^\s*([A-Za-z0-9_]+)\s*:\s*\{\s*name\s*:\s*["']?([^"'}]+?)["']?\s*\}\s*$/.exec(
      line,
    );
    if (match?.[1] !== undefined && match[2] !== undefined) {
      entries.push({ code: match[1], name: match[2].trim() });
    }
  }
  return entries;
};

/** One language studio knows about, and whether a run may be launched against it. */
export const LanguageReadiness = Schema.Struct({
  code: Schema.String,
  // From the registry when it has an entry, else the code itself — a pack whose files landed but
  // whose registry line did not is exactly the state this surface has to be able to show.
  name: Schema.String,
  // A code may be used as either side, and several are (`QAE` is an L2 pack and the L1 of every
  // pair). Both flags rather than one enum, so the New Curriculum modal can filter each select on
  // the side it is asking about.
  isL1: Schema.Boolean,
  isL2: Schema.Boolean,
  // L2 completeness only — the L2 side of `checkLanguagePack`, with no L1 named. The pair's L1 files
  // are judged again at launch, where the pair is known.
  complete: Schema.Boolean,
  blockers: Schema.Array(Schema.String),
  // The CEFR levels this L2's inventories can carry — see `inventoryLevels`. Absent when the
  // inventory is not something studio can read, which never locks a level.
  grammarLevels: Schema.optional(Schema.Array(Schema.String)),
  vocabLevels: Schema.optional(Schema.Array(Schema.String)),
});
export type LanguageReadiness = typeof LanguageReadiness.Type;

// ── Per-level inventory coverage ────────────────────────────────────────────────
// Steps 0i and 0v slice the pack's reference inventories by the run's CEFR level and hard-fail on
// an empty slice, minutes into a run. Studio holds both files, so it can refuse those levels up
// front instead. The rules mirror the pipeline's own slicers:
//
//   * grammar (`grammar-import.ts::extractSublevel`) is keyed per SUBLEVEL (`A1.1:`). A sublevel
//     needs its own block; a band (`A1`) needs a block for EVERY member (`A1.1` and `A1.2`).
//   * vocab (`step0.ts::runStep0v`) filters items on their `CEFR:` field by BAND, so `A1.1`, `A1.2`
//     and `A1` all need items tagged `A1`.
//
// The vocab inventory may instead ship as `full_vocab_inventory.reference.yml.zip` (QMS does), which
// the store cannot hold — so a missing vocab file means "unknown", not "empty". A missing grammar
// file has no such alternative and does mean empty.

export const GRAMMAR_INVENTORY_FILE = "grammar/full_grammar_inventory.reference.yml";
export const VOCAB_INVENTORY_FILE = "vocab/full_vocab_inventory.reference.yml";

const GRAMMAR_BLOCK_KEY = /^([ABC][12]\.[12]):\s*$/;
const VOCAB_CEFR_FIELD = /^\s*CEFR:\s*["']?([ABC][12])\b/gm;

/** The sublevels with at least one entry in a full grammar inventory. */
const grammarLevelsOf = (content: string): readonly string[] => {
  const levels: string[] = [];
  let current: string | undefined;
  for (const line of content.split("\n")) {
    const key = GRAMMAR_BLOCK_KEY.exec(line)?.[1];
    if (key !== undefined) {
      current = key;
    } else if (current !== undefined && /^\s*- /.test(line) && !levels.includes(current)) {
      levels.push(current);
    }
  }
  return levels;
};

/** The bands with at least one item in a vocab inventory. */
const vocabLevelsOf = (content: string): readonly string[] => [
  ...new Set(Array.from(content.matchAll(VOCAB_CEFR_FIELD), (match) => match[1] ?? "")),
];

/** Which CEFR levels an L2 pack's grammar and vocab inventories cover. */
export const inventoryLevels = (
  l2: string,
  files: readonly PackFile[],
): { readonly grammarLevels: readonly string[]; readonly vocabLevels?: readonly string[] } => {
  const grammar = files.find((file) => file.path === `L2_${l2}/${GRAMMAR_INVENTORY_FILE}`);
  const vocab = files.find((file) => file.path === `L2_${l2}/${VOCAB_INVENTORY_FILE}`);
  return {
    grammarLevels: grammar === undefined ? [] : grammarLevelsOf(grammar.content),
    ...(vocab === undefined ? {} : { vocabLevels: vocabLevelsOf(vocab.content) }),
  };
};

export type MissingInventory = "grammar" | "vocab";

/** The inventories that cannot carry a run at `level` — empty when both can, or are unknown. */
export const missingInventories = (
  readiness: Pick<LanguageReadiness, "grammarLevels" | "vocabLevels">,
  level: string,
): readonly MissingInventory[] => {
  const band = level.split(".")[0] ?? level;
  const needed = level.includes(".") ? [level] : [`${band}.1`, `${band}.2`];
  const missing: MissingInventory[] = [];
  const { grammarLevels, vocabLevels } = readiness;
  if (grammarLevels !== undefined && !needed.every((sub) => grammarLevels.includes(sub))) {
    missing.push("grammar");
  }
  if (vocabLevels !== undefined && !vocabLevels.includes(band)) missing.push("vocab");
  return missing;
};

/** What to tell a human about `missingInventories`: which file, and what it needs. */
export const missingInventoryMessage = (
  l2: string,
  level: string,
  missing: readonly MissingInventory[],
): string => {
  const band = level.split(".")[0] ?? level;
  const needs = missing.map((kind) =>
    kind === "grammar"
      ? `L2_${l2}/${GRAMMAR_INVENTORY_FILE} needs ${level.includes(".") ? `an ${level}` : `both ${band}.1 and ${band}.2`} block`
      : `L2_${l2}/${VOCAB_INVENTORY_FILE} needs items with CEFR: ${band}`,
  );
  return `${level} can't be generated for ${l2} yet: ${needs.join("; ")}. Fill it in the language pack first.`;
};

/**
 * Fold every pack file studio holds into the per-language answer the "Language taught" select
 * needs: which codes exist, what to call them, and whether each one's L2 pack can actually carry a
 * run.
 *
 * A code appears here if the registry names it OR a pack directory exists for it — deliberately the
 * union, because both halves of that state are wrong in an interesting way. A registry line with no
 * files is a language someone declared and never uploaded; files with no registry line is a pack
 * that will hard-exit at launch. Hiding either would hide the reason the option is unavailable.
 */
export const languageReadiness = (files: readonly PackFile[]): readonly LanguageReadiness[] => {
  const registry = files.find((file) => file.path === "language_codes.yml");
  const names = new Map(
    (registry === undefined ? [] : parseLanguageRegistry(registry.content)).map((entry) => [
      entry.code,
      entry.name,
    ]),
  );
  const { l1, l2 } = languageCodesOfPaths(files.map((file) => file.path));
  const codes = [...new Set([...names.keys(), ...l1, ...l2])].sort();
  return codes.map((code) => {
    const isL2 = l2.includes(code);
    // Only a code that HAS an L2 pack is judged as one. Running the L2 check over an L1-only code
    // (`QAE` is the L1 of every pair and has no `L2_QAE` tree in some deployments) would report it
    // as incomplete for missing L2 files it is not supposed to have — nonsense in the modal, and
    // the kind of nonsense a human learns to ignore.
    const blockers = isL2
      ? checkLanguagePack({ l2: code, files })
          .findings.filter((finding) => finding.severity === "blocker")
          .map((finding) => finding.message)
      : [];
    return {
      code,
      name: names.get(code) ?? code,
      isL1: l1.includes(code),
      isL2,
      complete: isL2 && blockers.length === 0,
      blockers,
      ...(isL2 ? inventoryLevels(code, files) : {}),
    };
  });
};

/** Add or replace one `CODE: { name: "…" }` line in the registry, preserving everything else. */
export const withRegistryEntry = (registry: string, code: string, name: string): string => {
  const entry = `${code}: { name: "${name}" }`;
  const lines = registry.split("\n");
  const index = lines.findIndex((line) => line.trim().startsWith(`${code}:`));
  if (index >= 0) {
    lines[index] = entry;
    return lines.join("\n");
  }
  const trimmed = registry.replace(/\n+$/, "");
  return `${trimmed}\n${entry}\n`;
};

// ── The upload's wire contract ──────────────────────────────────────────────────
// Here rather than in `apps/server` because both ends decode it: the server enforces it at the REST
// boundary, and the upload form renders the report it returns. A second copy in the web app would be
// the same drift these rules exist to prevent.

// A pack is a couple of dozen small markdown files, so both bounds sit far above any honest upload.
// They exist because the whole payload is held in memory and every file is written, and a JSON body
// with no bound at all is a memory-exhaustion risk — the same reasoning as `MAX_IMPORT_BYTES`, and
// deliberately the same number for the per-document cap. Declared here rather than imported from
// `document-create.ts` so the upload's limits read in one place; if they ever need to differ, they
// can, without the import implying they may not.
export const MAX_PACK_FILES = 200;
export const MAX_PACK_FILE_BYTES = 1_048_576;

export const UploadPackFile = Schema.Struct({
  // As the browser hands it over (`webkitRelativePath`), normalised on both sides. The client
  // normalises so it can preview the report; the server normalises again because the client's copy
  // is advice and the server's is enforcement.
  path: Schema.String.pipe(Schema.maxLength(512)),
  content: Schema.String.pipe(Schema.maxLength(MAX_PACK_FILE_BYTES)),
});
export type UploadPackFile = typeof UploadPackFile.Type;

// ADR-0118: an upload's persona images. Only the path and size travel in the JSON body — the report
// needs to know which images the pack WILL have, not their bytes — and each accepted image is then
// PUT as raw bytes to `/api/language-packs/blob`, where the size and type are enforced for real.
// A full identity kit is ~10 MB per pack; base64 inside one JSON body would be the wrong vehicle.
export const MAX_PACK_IMAGES = 300;

export const UploadPackImage = Schema.Struct({
  path: Schema.String.pipe(Schema.maxLength(512)),
  size: Schema.Number,
});
export type UploadPackImage = typeof UploadPackImage.Type;

export const UploadLanguagePackArgs = Schema.Struct({
  // The L2 the upload is FOR. Named explicitly rather than inferred from the file paths: an upload
  // may legitimately carry both an L2 and an L1 tree, and the report has to be about one pack.
  //
  // Branded, so a display name typed into the code field ("Mexican Spanish") is refused at the
  // decode boundary rather than creating an `L2_Mexican Spanish/` directory nothing can join on.
  l2: CurriculumLanguage,
  l1: Schema.optional(CurriculumLanguage),
  files: Schema.Array(UploadPackFile).pipe(Schema.maxItems(MAX_PACK_FILES)),
  images: Schema.optional(Schema.Array(UploadPackImage).pipe(Schema.maxItems(MAX_PACK_IMAGES))),
  // `CODE -> display name` for codes this upload introduces; written into `language_codes.yml`.
  displayNames: Schema.optional(Schema.Record({ key: Schema.String, value: Schema.String })),
  // Validate and report, write nothing. The form runs this on every file selection, so a human sees
  // the whole report before anything is durable.
  dryRun: Schema.Boolean,
  // Permit replacing documents the store already holds. Off by default.
  overwrite: Schema.Boolean,
});
export type UploadLanguagePackArgs = typeof UploadLanguagePackArgs.Type;

export const UploadLanguagePackResult = Schema.Struct({
  report: PackReport,
  // What this call actually wrote. Empty on a dry run and on a refusal — the two cases the caller
  // must not confuse with "nothing needed writing".
  written: Schema.Array(Schema.String),
  // True when the pack was written, or would have been on a dry run with no blockers.
  accepted: Schema.Boolean,
  // Every image the upload may store: the picked file (`from`, as sent) and the pack path it goes
  // to. The client PUTs exactly these after an accepted write, so it never decides the mapping.
  images: Schema.optionalWith(
    Schema.Array(Schema.Struct({ from: Schema.String, path: Schema.String })),
    { default: () => [] },
  ),
});
export type UploadLanguagePackResult = typeof UploadLanguagePackResult.Type;

// ── Known languages ─────────────────────────────────────────────────────────────
// Every Babbel language code studio knows a display name for. The template's `language_codes.yml`
// is generated from this, so a new pack arrives with its own registry line already written, and the
// upload form offers these codes rather than asking a human to remember them.
//
// The three codes the registry already held keep their stored names verbatim (`QMS`, `QAE`, `ITA`)
// — an upload must never rename a language as a side effect. The rest are Babbel's product
// languages. A registry line only names a language; a code with no pack files is listed as declared
// but not ready, never offered for generation.
export const KNOWN_LANGUAGES: readonly { readonly code: string; readonly name: string }[] = [
  { code: "QAE", name: "American English" },
  { code: "QMS", name: "Latin American Spanish" },
  { code: "ITA", name: "Standard Italian" },
  { code: "DAN", name: "Danish" },
  { code: "DEU", name: "German" },
  { code: "ENG", name: "English" },
  { code: "FRA", name: "French" },
  { code: "IND", name: "Indonesian" },
  { code: "NLD", name: "Dutch" },
  { code: "NOR", name: "Norwegian" },
  { code: "POL", name: "Polish" },
  { code: "POR", name: "Portuguese" },
  { code: "RUS", name: "Russian" },
  { code: "SPA", name: "Spanish" },
  { code: "SWE", name: "Swedish" },
  { code: "TUR", name: "Turkish" },
];

/** The display name studio knows for a code, if any. */
export const knownLanguageName = (code: string): string | undefined =>
  KNOWN_LANGUAGES.find((language) => language.code === code)?.name;

// ── The template ────────────────────────────────────────────────────────────────
// A pack skeleton that mirrors the real packs file for file and section for section (`L2_ITA`,
// `L1_QAE`), generated from the very constants the rules above check, so the two cannot drift: a
// freshly downloaded template uploads with zero blockers by construction, and every remaining gap is
// a `<<FILL: …>>` warning naming its file.
//
// Everything that is STRUCTURE is written out in full: headings the prompts reference by name, the
// bold field markers the validators key on, the HTML markers the parsers read, each YAML file's
// exact shape, and the cast identities — persona ids the exercise schema accepts, not linguistic
// content. Everything that is KNOWLEDGE of the language is a placeholder (ADR-0021): what a persona
// sounds like and looks like, which register forms exist, which words are function words.

// The canonical cast every pack starts from (ADR-0118). The ids are the ones the exercise schema's
// closed `PersonaId` enum accepts (`SCHEMA_PERSONA_IDS`), so a template-built pack produces content
// the pipeline keeps; names, prose, voices and images are the pack's own to change. A copy of the
// `persona-config` cast — it goes with that package (ADR-0118 slice 5), when the enum opens.
const CANONICAL_CAST = [
  {
    id: "juan",
    name: "Juan",
    gender: "male",
    age: "middle-aged",
    role: "workplace contact / older mentor figure",
    register: "formal",
  },
  {
    id: "luis",
    name: "Luis",
    gender: "male",
    age: "young",
    role: "peer / friend",
    register: "informal",
  },
  {
    id: "laura",
    name: "Laura",
    gender: "female",
    age: "young",
    role: "peer / community connector",
    register: "informal",
  },
  {
    id: "ana",
    name: "Ana",
    gender: "female",
    age: "middle-aged",
    role: "teacher / mentor",
    register: "formal-in-class",
  },
] as const;
const CANONICAL_TUTORS = [
  { id: "elena", name: "Elena", gender: "female", age: "middle-aged" },
  { id: "jose", name: "Jose", gender: "male", age: "middle-aged" },
] as const;

// Every image slot a persona can carry, as the READMEs list them.
const IMAGE_SLOT_LIST = PERSONA_IMAGE_SLOTS.join(", ");

// `personas.yml` for a template: identities written out, everything a human must decide a
// placeholder. Rendered through `renderPersonasFile`, so the file parses by construction and the
// upload's registry check has nothing to block on — only `<<FILL` warnings remain.
const personaRegistry = (
  entries: readonly {
    readonly id: string;
    readonly name: string;
    readonly gender: "male" | "female";
    readonly age: "young" | "middle-aged";
  }[],
  role: "main-character" | "tutor",
): string =>
  renderPersonasFile({
    lighting: fill(
      "the lighting spec every persona image is generated under, e.g. soft, even, high-key daylight on a plain white background",
    ),
    personas: entries.map((entry) => ({
      id: PersonaId.make(entry.id),
      display_name: entry.name,
      role,
      gender: entry.gender,
      age: entry.age,
      description: fill(
        "appearance prose for the image model: build and posture, face and hair, clothing and accessories",
      ),
      voice: {
        provider: "elevenlabs",
        voice_id: fill(`ElevenLabs voice id for ${entry.name}, speaking this language`),
        speed: 1,
      },
      deprecated: false,
    })),
  });

// The L1 every existing pair is taught to, and so the gloss column a vocab inventory needs when no
// L1 is named.
const DEFAULT_L1 = "QAE";

const fill = (what: string): string => `${FILL_MARKER} ${what}>>`;

const displayName = (code: string): string =>
  knownLanguageName(code) ?? fill(`display name of ${code}`);

const LANGUAGE_CODES = (codes: readonly string[]): string => {
  const extra = codes.filter((code) => knownLanguageName(code) === undefined);
  return [
    "# Language-code registry — maps the Babbel-internal codes passed via the",
    "# pipeline's --l1 / --l2 parameters to a human display name (the L1 / L2",
    "# prompt variables). Codes are NOT a public standard. A code may be used",
    "# as either L1 or L2.",
    "#",
    "# On upload, only codes studio does not already have are added; an existing",
    "# language's name is never changed from here (use the form's Display name).",
    "#",
    '#   <CODE>: { name: "<display name>" }',
    "",
    ...KNOWN_LANGUAGES.map((language) => `${language.code}: { name: "${language.name}" }`),
    ...extra.map((code) => `${code}: { name: "${displayName(code)}" }`),
    "",
  ].join("\n");
};

const marker = (key: string, what: string): string => `<!-- ${key}: ${fill(what)} -->`;

const LANGUAGE_PROFILE = (l2: string, l1: string): string =>
  [
    `# Language profile — L2_${l2} (${displayName(l2)})`,
    "",
    `Pack manifest for the L2 = **${displayName(l2)}** (\`${l2}\`) curriculum. This file`,
    "registers the L2-specific conventions the pipeline relies on; the cast, scene",
    "model, and culture content live in the sibling files named below. It is inlined",
    "WHOLE into the grammar-inventory and exercise-generation prompts, so write it for",
    "a reader who knows nothing about this language. `L2_ITA` and `L2_QMS` in studio",
    "show the depth expected.",
    "",
    `- **Display name:** ${displayName(l2)} (from \`language_codes.yml\`).`,
    `- **Register system:** ${fill('the ACTUAL address forms and when each is used — e.g. "informal tu vs. formal Lei" — never "the appropriate formal register"')}`,
    "  Per-character register and the unit-level register-contrast rules are defined in",
    "  `personas.md` (`Register with learner`, `Cast distribution rules`).",
    "- **Scenario / scene model:** the `A1` (in-culture everyday) / `A2` (in-culture",
    `  transactional) / \`B\` (local encounter, ${fill("the diaspora community in the learner's country")})`,
    "  archetypes, the per-persona `Default scene contexts`, and the learner-positioning",
    "  rules all live in `personas.md`.",
    "- **Culture & pragmatics:** AHA gate + module shape in the language-agnostic",
    "  `inputs/authoring_guides/CP_Module_Authoring_Guide.md`.",
    `- **Grammar:** ${fill("whether an upstream grammar-inventory record exists for this language; if not, say to use the generated grammar-source arm (steps 0s + 0a), driven by the Grammar inventory notes below")}`,
    "",
    "## Pack files",
    "",
    "| File | Role |",
    "|------|------|",
    "| `personas.md` | Recurring cast, register, scene archetypes, learner positioning |",
    "| `reference_dialogues.md` | Approved dialogue exemplars (style anchors) |",
    "| `vocab/full_vocab_inventory.reference.yml` | CEFR vocab reference (step 1) |",
    "| `gender_pair_exclude.yml` | Gendered noun pairs exempt from the gendered-pair gate |",
    "| `country_names.yml` | Country names exempt from the lowercase-gloss check |",
    "| `function_words.yml` | L2 function words (leaked-L2-prose detection) |",
    "| `decisive_orthography.yml` | Characters that decisively mark text as L2 |",
    "| `speaker_prelude_verbs.yml` | Verbs marking a dialogue speaker-attribution prelude |",
    "",
    "## Grammar inventory notes",
    "",
    "L2-specific addenda for grammar inventory generation (steps 0s / 0a) and review, per",
    "`inputs/authoring_guides/Grammar_Inventory_Authoring_Guide.md` (which is",
    "language-agnostic and defers to this section).",
    "",
    "### Verb-entry allowlist (guide §2.2)",
    "",
    "Only these lemmas may be named in an entry title as *the* teachable object, from the",
    "sublevel indicated. Every other verb appears under conjugation-pattern or",
    "irregular-subgroup titles.",
    "",
    "| Verb | Rationale | From |",
    "|---|---|---|",
    `| ${fill("verb")} | ${fill("why it earns its own entry (unique paradigm, foundational use)")} | A1.1 |`,
    `| ${fill("verb")} | ${fill("why it earns its own entry")} | A1.2 |`,
    "",
    "### A1.1 deferrals and limits (tighten guide §4)",
    "",
    `- ${fill("what is held back from A1.1 and to which level — periphrases, clitics, imperatives, preposition grids, clock/calendar limits, irregular plurals")}`,
    "",
    "### Example register policy (guide §2.4)",
    "",
    `- Variety: ${fill("the standard variety taught, and which regional forms are excluded")}`,
    `- Settings, names, and toponyms: ${fill("the country and diaspora the examples are set in")}, consistent with the persona world in \`personas.md\`.`,
    "- Examples are L2-only; no L1 words inside example strings (numbers and times written",
    "  in the L2).",
    "",
    "## Dialogue notes",
    "",
    "L2-specific addenda for dialogue authoring (the exercise-generation step), per",
    "`inputs/authoring_guides/Dialogue_Module_Authoring_Guide.md` (which is",
    "language-agnostic and defers to this section). Address/register and per-character",
    "dialect are NOT restated here — they live in the register system above and in",
    "`personas.md`.",
    "",
    "### Voice markers (A1.1 calibration)",
    "",
    "Cap at **1–2 per dialogue** (even allowed ones — cumulative density is the real risk).",
    "",
    `- **Allow-set (non-gap turns):** ${fill("high-frequency, transparent, pan-regional fillers")}`,
    `- **Deny-set at A1.1:** ${fill("region-narrow or opaque markers a beginner cannot decode")}`,
    `- **Figurative-idiom ban is absolute** at A1.1, even as passive context. ${fill("two or three idioms to avoid")}`,
    "",
    "### Common correctness traps (front-loaded from step-4 axis 7)",
    "",
    "Flat errors that mislead an A1 learner; the step-4 polish flags them but does not",
    "auto-fix gap-target errors — avoid producing them here.",
    "",
    `- ${fill("a trap: the wrong form, the right form, and why (agreement, verb choice, clock or number agreement, response licensing)")}`,
    "",
    "### Dictate homophone cautions (`dialogue_choose_dictate`)",
    "",
    fill(
      "which sound contrasts are hard to hear for this audience, so dictate distractors do not hinge on them",
    ),
    "",
    "### Numeric-formation boundaries",
    "",
    fill("number-formation quirks a distractor numeral must not cross before they are taught"),
    "",
    `### L1-interference watch-outs (current audience L1 = ${displayName(l1)})`,
    "",
    `- ${fill("false friends and calques from the audience L1: the L2 word, what it really means, and the word the learner actually wants")}`,
    "",
    "## Parser configuration",
    "",
    "Machine-read markers. Each is a lowercase, comma-separated list on ONE line; edit the",
    "list, never the key. When a marker is missing the runtime silently keeps the SPANISH",
    "defaults, so every one below matters.",
    "",
    "- **Essential first-flagship verbs** — the core verbs whose paradigm establishes a",
    "  pattern at A1.1, so they must drip singular-first (`md_parse.py`).",
    "",
    marker("essential-flagship-verbs", "the copula and the other core verbs"),
    "",
    "- **Scene-demonym stopwords** — demonyms and language names that may legitimately",
    "  appear in a grammar/C&P explanation, so `SCENE_LEAK_IN_EXPLANATION` does not flag",
    "  them. Do NOT list place names — a leaked city should fire.",
    "",
    marker("scene-demonym-stopwords", "language names and demonyms in English"),
    "",
    "- **Language rules for the TypeScript runtime** — the same five lists as the `.yml`",
    "  files beside this one, which the Python runtime reads instead. Keep each marker in",
    "  step with its file. `decisive-orthography` is the one exception to the list rule:",
    "  its value is used verbatim as a regex character class, not split or lower-cased.",
    "",
    marker("l2-function-words", "same entries as function_words.yml"),
    marker("country-names", "same entries as country_names.yml (case is kept)"),
    marker("speaker-prelude-verbs", "same entries as speaker_prelude_verbs.yml"),
    marker("gender-pair-exclude", "same entries as gender_pair_exclude.yml"),
    marker("decisive-orthography", "same characters as decisive_orthography.yml, no separators"),
    "",
  ].join("\n");

const personaBlock = (persona: (typeof CANONICAL_CAST)[number]): string =>
  [
    "---",
    "",
    `## ${persona.name}`,
    "",
    `- **Age / origin:** ${fill("age range; home city and region")}`,
    `- **Role:** ${persona.role} — ${fill("occupation, and what they are like at it")}`,
    `- **Register with learner:** ${fill(`the actual ${persona.register} address form the learner uses, and when it changes`)}`,
    `- **Voice / quirks:** ${fill("temperament, favourite topics, a habit or two")}`,
    `- **Regional / dialect features:** ${fill("pronunciation, signature lexicon with glosses, politeness habits")}`,
    "- **Default scene contexts (A1 / A2 / B):**",
    `  - *A1 (in-culture everyday):* ${fill("three named everyday settings in their home city or region")}`,
    `  - *A2 (in-culture transactional):* ${fill("three named service settings: airport, pharmacy, ticket window, hotel")}`,
    `  - *B (local encounter, diaspora community):* ${fill("three named settings in the learner's country")}`,
    "",
  ].join("\n");

const PERSONAS = (l2: string): string =>
  [
    "# Recurring user personas",
    "",
    "This file defines the curriculum's recurring cast; every unit plan draws its named",
    "characters from this set, with `(one-off cameo)` for one-time characters.",
    "",
    "- `Register with learner` states how the *learner* addresses this character",
    "  (asymmetric on purpose).",
    "- The recurring cast is fixed at **4 main characters** — do not invent additional",
    "  recurring characters. The tutoring voices that speak the L1 `tutor_*` explanations",
    "  are not scene characters; they live in the L1 pack (`L1_<l1>/tutors.md`).",
    "",
    "<!-- TEMPLATE: each persona heading must match a `display_name` of an active",
    "main-character in the sibling `personas.yml`, which carries the machine-read half of",
    "the cast (id, gender, voice, appearance). Write each bio for THIS language and culture.",
    "Rename a persona in both files at once; keep the ids in `personas.yml` as they are. -->",
    "",
    REQUIRED_PERSONA_SECTIONS[0],
    "",
    `The learner is a ${fill("where the learner lives and what their first language is")} studying ${displayName(l2)} to`,
    `(a) ${fill("interact with speakers they meet at home")} and (b) ${fill("travel to or work with people where it is spoken")}.`,
    "**The learner is never a named character in any scene narrative.** Integrated",
    "Dialogues, Communicative situations, Named settings, and L1 scene-setting paragraphs",
    'are third-person about the recurring personas — no "the learner", "you", or "we".',
    "Speaking Challenge `Learner: ___ (→ …)` lines are a practice-UX convention and are",
    "the sole exception.",
    "",
    ...CANONICAL_CAST.map(personaBlock),
    "---",
    "",
    REQUIRED_PERSONA_SECTIONS[1],
    "",
    "Rules for the planner.",
    "",
    "- Every persona in this file should appear in **≥ 2 units**; if a draft leaves a",
    "  persona unused or under-used, place them in the unit where they fit most naturally.",
    `- A single unit's cast should mix register: at least one ${fill("informal address form")} persona and one`,
    `  ${fill("formal address form")} persona per unit when the unit goal admits register contrast.`,
    "- A persona's first appearance in the curriculum is labeled `(NEW)`; subsequent",
    "  appearances are returning.",
    "- One-off cameo characters (a server, a vendor, a one-time clerk) are allowed; label",
    "  them `(one-off cameo)` so they are not added to the recurring inventory.",
    "- **Scenario archetypes (A1 / A2 / B).** Tag every learning cycle in its heading with",
    "  one archetype: `A1` (in-culture everyday, set in the anchor persona's home region),",
    `  \`A2\` (in-culture transactional/service, same country), or \`B\` (local encounter in ${fill("the diaspora community")}).`,
    "  Target curriculum-wide mix ≈ 25% A1 / 35% A2 / 40% B; every unit covers ≥2",
    "  archetypes; no run of 3+ consecutive units in a single archetype.",
    "- **Settings from `Default scene contexts`.** For an A1 or A2 cycle, pick the city from",
    "  the anchor's `(A1)` / `(A2)` list; for a B cycle, from `(B)`. Persona dialect is fixed",
    "  by origin in all three archetypes.",
    "- **Learner-as-character.** See `## Learner positioning` above.",
    "",
  ].join("\n");

const exemplarLine = (speaker: string, what: string, answer?: string): string =>
  `- ${speaker}: ${fill(what)}${answer === undefined ? "" : ` (→ ${fill(answer)})`}`;

const REFERENCE_DIALOGUES = (): string =>
  [
    "# Reference Dialogues — approved exemplars (style anchors)",
    "",
    `**Source:** ${fill("where these came from: hand-drafted, or extracted from an accepted run")}`,
    "**Use:** style anchors for the exercise-generation step's dialogue authoring — match",
    "their register, rhythm, and naturalness. Content (vocab, gap targets) always follows",
    "the current unit's KV, never these exemplars.",
    "**Cast caveat:** for casting, names, and dialect always follow the current",
    "`personas.md`, never these exemplars.",
    "",
    "<!-- TEMPLATE: one turn per line as `- Speaker: text`. A gap is written as three",
    "underscores in backticks inside the line, and its answer follows as `(→ answer)` at",
    "the end of the line. Speakers are cast names from personas.md, a cameo, or Learner. -->",
    "",
    "## Exemplar 1 — `dialogue_choose_dictate`",
    "",
    exemplarLine("Laura", "a line with one `___` gap", "the missing word"),
    exemplarLine("Juan", "a reply with one `___` gap", "the missing word"),
    exemplarLine("Laura", "a follow-up question"),
    exemplarLine("Juan", "a reply with one `___` gap", "the missing word"),
    exemplarLine("Laura", "a closing line"),
    "",
    "## Exemplar 2 — `dialogue_choose_item`",
    "",
    exemplarLine("Juan", "a greeting and introduction"),
    exemplarLine("Visitor", "a question with one `___` gap", "the missing word or phrase"),
    exemplarLine("Juan", "a reply with one `___` gap", "the missing word"),
    exemplarLine("Visitor", "a closing line"),
    "",
    "## Exemplar 3 — `dialogue_speak_default`",
    "",
    exemplarLine("Ana", "an opening line setting the scene"),
    `- Learner: ${fill("a reply containing the **bolded target phrase**")}`,
    exemplarLine("Ana", "a reaction"),
    `- Learner: ${fill("a second reply with **another bolded target**")}`,
    exemplarLine("Ana", "a closing line"),
    "",
  ].join("\n");

// Each rule file's header (after `L2_ITA`'s), and the profile marker that is its TypeScript twin.
const RULE_FILE_DOC: Record<(typeof LANGUAGE_RULE_FILES)[number], readonly [string, string]> = {
  "function_words.yml": [
    "L2 function words used to detect L2 prose leaking into an authored L1\n# `explain:`/`correct:` field. Lowercase.",
    "l2-function-words",
  ],
  "country_names.yml": [
    "Country names (cased) recognised as proper nouns by\n# `lexicon_gloss_not_lowercase`'s exempt-from-lowercase-gloss-check list.\n# List them in the L2 AND in the audience L1.",
    "country-names",
  ],
  "speaker_prelude_verbs.yml": [
    'Speaker-prelude verbs used to detect a dialogue carrier line that embeds\n# its own speaker attribution (e.g. "Maria says: ..."). Lowercase.',
    "speaker-prelude-verbs",
  ],
  "gender_pair_exclude.yml": [
    "Distinct-referent nouns / paradigm forms exempt from the gendered-pair gate\n# (e.g. grandfather/grandmother: not an agreement pair, the learner needs\n# both forms). Lowercase.",
    "gender-pair-exclude",
  ],
  "decisive_orthography.yml": [
    "Characters that, if present, decisively classify a span of text as this\n# L2 rather than the audience L1 — used verbatim as a regex character class,\n# never split or lower-cased. ONE quoted string, NOT a list: a list here\n# crashes the run.",
    "decisive-orthography",
  ],
};

const ruleFile = (name: (typeof LANGUAGE_RULE_FILES)[number]): string => {
  const [doc, twin] = RULE_FILE_DOC[name];
  return [
    `# ${doc}`,
    "#",
    "# Read by the Python runtime. The TypeScript runtime reads the",
    `# \`<!-- ${twin}: … -->\` marker in language_profile.md instead; keep both in step.`,
    "# Deleting every entry is NOT neutral: the runtime then falls back to the",
    "# SPANISH list rather than failing.",
    ...(name === DECISIVE_ORTHOGRAPHY
      ? [`"${fill("the characters, no separators")}"`]
      : // Quoted: an unquoted `<<FILL: x>>` is a YAML mapping (`: ` inside a plain scalar), so an
        // unedited list would parse as dicts rather than as the strings the runtime expects.
        [`- "${fill("first entry")}"`, `- "${fill("one entry per line")}"`]),
    "",
  ].join("\n");
};

const VOCAB = (l2: string, l1: string): string =>
  [
    "# CEFR vocab reference inventory — inlined into the step-1 prompts, so a pack",
    "# without it fails at step 1. One row per lexeme under `items:`. `CEFR` is the",
    "# major level (A1, A2, B1 …), not a sublevel. `gender` is masculine / feminine /",
    `# neuter, or "" where it does not apply. \`item_${l1}\` is the gloss in the audience L1.`,
    `note: "${fill("where this list comes from and which levels it covers")}"`,
    "items:",
    ...[1, 2].flatMap((n) => [
      `- item_${l2}: "${fill(`lexeme ${n}`)}"`,
      `  item_${l1}: "${fill("gloss")}"`,
      `  part of speech: "${fill("noun / verb / adjective / adverb / preposition")}"`,
      "  CEFR: A1",
      `  gender: "${fill("masculine / feminine / empty")}"`,
    ]),
    "",
  ].join("\n");

const L1_PROFILE = (l1: string): string =>
  [
    `# L1 profile — L1_${l1} (${displayName(l1)})`,
    "",
    "Audience-L1 conventions, shared across every L2 that this L1 learns.",
    "",
    `- **Display name:** ${displayName(l1)} (from \`language_codes.yml\`).`,
    "- **Gloss language:** all L1 glosses, L1 scene-setting, and `tutor_choose_default`",
    `  rule explanations are written in ${displayName(l1)}. The prompts gloss in the run's`,
    "  L1, so this needs no separate wiring — it is documented here for clarity.",
    "",
    "## Parser configuration",
    "",
    "The match-exercise parser drops L1 pronoun glosses that appear as noise beside L2",
    "forms. The set below is read by `scripts/convert_unit_plan_md_to_json.py` (via",
    "`pipeline.lang.l1_pronoun_noise`). Without it the parser silently falls back to the",
    "ENGLISH set. Lowercase, comma-separated, on one line.",
    "",
    marker("pronoun-noise", "this language's subject pronouns in lowercase"),
    "",
  ].join("\n");

const TUTORS = (l1: string): string =>
  [
    `# Tutoring voices (L1: ${displayName(l1)})`,
    "",
    "The tutors who deliver the spoken L1 explanations in the `tutor_*` exercises",
    "(grammar rules, culture/pragmatic notes). They are an **L1 property**, shared across",
    "every L2 this audience studies — which is why they live in the L1 pack, not in any",
    "L2 `personas.md`.",
    "",
    "They are **not** scene characters: they never appear in dialogues or any L2 content,",
    "and have no register, dialect, origin, or `Default scene contexts` model.",
    "",
    "Each tutor below is a `## <FirstName>` H2 block declaring `**Voice / tone:**` (the",
    "marker the cast gate keys on). Keep the names exactly as written — they are the",
    "`tutor` personas in the sibling `personas.yml`, and any other name is refused.",
    "",
    "---",
    "",
    ...CANONICAL_TUTORS.map((tutor) => tutor.name).flatMap((name) => [
      `## ${name}`,
      "",
      `- ${TUTOR_VOICE_MARKER} ${fill("temperament and how they explain, e.g. example-first, or names the interference trap before the rule")}`,
      "",
    ]),
  ].join("\n");

// Where a template's starter images come from: the packs that hold the canonical cast's own identity
// kits. The template keeps the canonical ids, so each persona starts looking like the character it
// was copied from — a starting point to replace, not a requirement.
const REFERENCE_L2 = "QMS";
const REFERENCE_L1 = "QAE";

/**
 * Each template persona's image directory, paired with the reference pack's directory for the same
 * id. The download copies `from/<slot>.<ext>` to `to/<slot>.<ext>`; a slot the reference lacks is
 * simply absent, which only warns.
 */
export const templatePersonaImageSources = (
  l2: string,
  l1?: string,
): readonly { readonly from: string; readonly to: string }[] => [
  ...CANONICAL_CAST.map((persona) => ({
    from: `L2_${REFERENCE_L2}/personas/${persona.id}/`,
    to: `L2_${l2}/personas/${persona.id}/`,
  })),
  ...(l1 === undefined
    ? []
    : CANONICAL_TUTORS.map((tutor) => ({
        from: `L1_${REFERENCE_L1}/personas/${tutor.id}/`,
        to: `L1_${l1}/personas/${tutor.id}/`,
      }))),
];

/**
 * The identity-kit image paths a template's personas can carry, for the archive README to list —
 * `<pack>/personas/<id>/<slot>.webp` per persona. The download fills them from
 * `templatePersonaImageSources`; a pack without them only warns.
 */
export const templatePersonaImageHints = (
  l2: string,
  l1?: string,
): { readonly slots: string; readonly dirs: readonly string[] } => ({
  slots: IMAGE_SLOT_LIST,
  dirs: [
    ...CANONICAL_CAST.map((persona) => `L2_${l2}/personas/${persona.id}/`),
    ...(l1 === undefined ? [] : CANONICAL_TUTORS.map((tutor) => `L1_${l1}/personas/${tutor.id}/`)),
  ],
});

/**
 * The skeleton pack for a new language, as `(path, content)` pairs ready to be zipped.
 *
 * The L1 side is included only when an L1 code is given: an L1 pack is shared by every L2 that
 * audience learns and usually exists already. The download also leaves out any file the store
 * already holds (see `templateArchive`), so an existing L1 is never offered for overwriting.
 *
 * `language_codes.yml` carries every known language plus any new code. That is safe to upload
 * because the upload folds registry lines in ADD-ONLY: a code the store already has keeps its
 * stored name.
 */
export const templateLanguagePack = (l2: string, l1?: string): readonly PackFile[] => [
  { path: "language_codes.yml", content: LANGUAGE_CODES(l1 === undefined ? [l2] : [l2, l1]) },
  { path: `L2_${l2}/language_profile.md`, content: LANGUAGE_PROFILE(l2, l1 ?? DEFAULT_L1) },
  { path: `L2_${l2}/personas.md`, content: PERSONAS(l2) },
  { path: `L2_${l2}/personas.yml`, content: personaRegistry(CANONICAL_CAST, "main-character") },
  { path: `L2_${l2}/reference_dialogues.md`, content: REFERENCE_DIALOGUES() },
  { path: `L2_${l2}/${VOCAB_INVENTORY}`, content: VOCAB(l2, l1 ?? DEFAULT_L1) },
  ...LANGUAGE_RULE_FILES.map((name) => ({ path: `L2_${l2}/${name}`, content: ruleFile(name) })),
  ...(l1 === undefined
    ? []
    : [
        { path: `L1_${l1}/profile.md`, content: L1_PROFILE(l1) },
        { path: `L1_${l1}/tutors.md`, content: TUTORS(l1) },
        { path: `L1_${l1}/personas.yml`, content: personaRegistry(CANONICAL_TUTORS, "tutor") },
      ]),
];
